<template>
    <div data-testid="step-Login" class="container">
        <h1 class="container__main-header">
            {{ $t(`${TRANSLATION_KEY}.logInAccount`) }}
        </h1>
        <div class="container__main-paragraph">
            <span>{{ $t(`${TRANSLATION_KEY}.or`) }}</span>
            <router-link to="/" @click="() => handleClick()">
                {{ $t(`${TRANSLATION_KEY}.createNewAccount`) }}
            </router-link>
        </div>
        <div v-if="isEmail && !isClicked">
            <div v-if="messageInfo" class="container__mention">
                {{ messageInfo }}
            </div>
            <div v-else class="container__mention">
                {{ $t(`${TRANSLATION_KEY}.yourAccountIsExist`) }}
            </div>
        </div>
        <form autocomplete="off" @submit.prevent="null">
            <div class="row">
                <Field
                    v-model="formData.loginEmail"
                    type="email"
                    name="loginEmail"
                    :placeholder="$t(`${TRANSLATION_KEY}.emailPlaceholder`)"
                    cy-data="login-email"
                    :allow-all-characters="true"
                >
                    <span>{{ $t(`${TRANSLATION_KEY}.email`) }}</span>
                </Field>
            </div>
            <div class="row">
                <Field
                    v-model="formData.loginPassword"
                    type="password"
                    name="loginPassword"
                    :placeholder="$t(`${TRANSLATION_KEY}.existingPassword`)"
                    cy-data="login-password"
                    :allow-all-characters="true"
                >
                    <span>{{ $t(`${TRANSLATION_KEY}.password`) }}</span>
                </Field>
            </div>
            <div class="row row--remind-password">
                <a
                    :href="$t(`${TRANSLATION_KEY}.remindPasswordLink`)"
                    cy-data="remind-password"
                >
                    {{ $t(`${TRANSLATION_KEY}.remindPassword`) + ' \xbb' }}
                </a>
            </div>
            <SubmitButton
                :action="Action.LOGIN"
                :button-value="$t(`${TRANSLATION_KEY}.logIn`)"
                :data="formData"
                data-testid="btn-Login-submit"
                class="button button--submit"
                :disabled="!(formData.loginEmail && formData.loginPassword)"
                @click="login"
            />
        </form>
    </div>
</template>

<script lang="ts">
import { defineComponent, reactive, computed, onMounted, ref } from 'vue';
import { useStore } from 'vuex';
import SubmitButton from '@/components/basic/buttons/SubmitButton.vue';
import Field from '@/components/basic/fields/Field.vue';
import { SET_PROGRESS, SET_STEP } from '@/constants/mutation-types';
import router from '@/router';
import i18n from '@/i18n';
import { PROGRESS_BAR_TIMES } from '@/constants/progress-bar-times';
import { Action } from '@/constants/ajax';
import {
    AmplitudeAnalyticsEvents,
    amplitudeSendEvent,
} from '@/utils/AmplitudeAnalyticsEvents';

const TRANSLATION_KEY = 'steps.Login';

enum KeyWords {
    IS_RETURNING = 'isReturning',
}

export default defineComponent({
    name: 'Login',
    components: {
        Field,
        SubmitButton,
    },
    setup() {
        const store = useStore();

        const formData = reactive({
            loginEmail: '',
            loginPassword: '',
            residenceCountry: '',
        });

        const isClicked = ref(false);
        if (!store.state.residenceCountry) {
            if (i18n.global.locale.value.toUpperCase() === 'ES') {
                formData.residenceCountry = 'ES';
            } else {
                router.push('/');
            }
        } else {
            formData.residenceCountry = store.state.residenceCountry;
        }
        const isEmail = computed(() => store.state.formData.loginEmail);
        const messageInfo = computed(() => store.state.formData.message);

        const login = () => {
            amplitudeSendEvent(
                AmplitudeAnalyticsEvents.newLogin.LOGIN_CONTINUE
            );
            isClicked.value = true;

            if (!window.localStorage.getItem(KeyWords.IS_RETURNING)) {
                window.localStorage.setItem(KeyWords.IS_RETURNING, 'true');
            }
        };

        function handleClick(): void {
            store.commit(SET_STEP, 'newAccount');
        }

        onMounted(() => {
            store.commit(SET_PROGRESS, PROGRESS_BAR_TIMES.LOGIN);
        });

        return {
            formData,
            isClicked,
            isEmail,
            messageInfo,
            TRANSLATION_KEY,
            Action,
            login,
            handleClick,
        };
    },
});
</script>
